import { type FC, type ReactNode, use, useCallback, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@heroui/react";
import { Plus } from "@gravity-ui/icons";

import { CalendarEventsContext } from "@domain/context/CalendarEventsContext";
import { CompanyContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { UserContext } from "@domain/context/UserProvider";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { ProductKeys } from "@domain/models/Product";
import { useCanWrite } from "@ui-components/hooks/usePermissions";
import { GlobalYearSelect } from "@accounting/shared/GenericTableShell/Toolbar/components/YearSelect";
import { useHomeCalendar } from "@accounting/common/calendar/useHomeCalendar";
import type { CalendarEvent } from "@accounting/common/calendar/types";
import {
    CollapsedRow,
    ExpandedRow,
    GROUP_LABEL_ID,
    GROUP_ORDER,
} from "@accounting/common/calendar/components/EventRow";
import { CalendarEventFormModal } from "@accounting/common/calendar/components/CalendarEventFormModal";
import { HomeWidgetShell } from "@app/pages/home/HomeWidgetShell";
import { HomeExpandToggle } from "@app/pages/home/HomeExpandToggle";

const Skeleton: FC = () => (
    <div
        data-testid="home-calendar-skeleton"
        className="HomeCalendarWidget w-120 rounded-2xl bg-app-surface/50 backdrop-blur-md border border-app-surface/80 shadow-lg p-4"
    >
        <div className="animate-pulse space-y-3">
            <div className="h-3 w-24 rounded bg-app-text/20" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-full rounded bg-app-text/10" />
            <div className="h-3 w-2/3 rounded bg-app-text/10" />
        </div>
    </div>
);

export const HomeCalendarWidget: FC = () => {
    const { companyGQL } = use(CompanyContext);
    const user = use(UserContext);
    const { isReady } = use(ReadinessContext);
    const { events: userEventsGql } = use(CalendarEventsContext);
    const canWriteCalendar = useCanWrite(ProductKeys.AccountingCommon);
    const intl = useIntl();

    const { groups, summary, total } = useHomeCalendar();

    const [expanded, setExpanded] = useState(false);
    const toggle = useCallback(() => setExpanded((v) => !v), []);

    // "+" opens create; clicking a user row (source: "user") opens edit prefilled from
    // the raw provider event (the widget-shape CalendarEvent has no notes/type/assignee).
    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const editingEvent = useMemo(() => userEventsGql.find((e) => e.id === editingId), [userEventsGql, editingId]);
    const openCreate = useCallback(() => {
        setEditingId(null);
        setModalOpen(true);
    }, []);
    const openEdit = useCallback((event: CalendarEvent) => {
        setEditingId(event.stateKey);
        setModalOpen(true);
    }, []);
    const closeModal = useCallback(() => {
        setModalOpen(false);
        setEditingId(null);
    }, []);

    if (!ProductAccessUtils.canRead(ProductKeys.AccountingCommon, companyGQL, user)) {
        return null;
    }
    if (!isReady) {
        return <Skeleton />;
    }

    const summaryNode: ReactNode = (() => {
        if (summary.kind === "today") {
            return <FormattedMessage id="app.home.calendar.summary.today" values={{ count: summary.count ?? 0 }} />;
        }
        if (summary.kind === "next" && summary.nextEvent) {
            const title = intl.formatMessage({ id: summary.nextEvent.titleId }, summary.nextEvent.titleValues);
            return (
                <FormattedMessage
                    id="app.home.calendar.summary.next"
                    values={{ title, days: summary.daysUntil ?? 0 }}
                />
            );
        }
        return <FormattedMessage id="app.home.calendar.summary.empty" />;
    })();

    const collapsedEvents = GROUP_ORDER.flatMap((g) => groups[g]).slice(0, 3);
    const nonEmptyGroups = GROUP_ORDER.filter((g) => groups[g].length > 0);
    const isEmpty = total === 0;

    const header = (
        <div className="flex w-full items-center justify-between gap-4">
            <span className="flex items-center gap-3">
                <GlobalYearSelect />
                <span className="text-sm font-bold uppercase tracking-wide opacity-70">
                    <FormattedMessage id="app.home.calendar.title" />
                </span>
            </span>
            <span className="flex shrink-0 items-center gap-1">
                <Button
                    size="sm"
                    variant="ghost"
                    isIconOnly
                    isDisabled={!canWriteCalendar}
                    onPress={openCreate}
                    aria-label={intl.formatMessage({ id: "app.home.calendar.addEvent" })}
                    className="HomeCalendarWidget__add"
                >
                    <Plus className="size-4" />
                </Button>
                <HomeExpandToggle expanded={expanded} onToggle={toggle} isDisabled={isEmpty} />
            </span>
        </div>
    );

    const subHeader = <div className="mt-1 text-xs opacity-60">{summaryNode}</div>;

    const collapsedBody = isEmpty ? null : (
        <div className="mt-3 space-y-2">
            {collapsedEvents.map((event) => (
                <CollapsedRow
                    key={event.id}
                    event={event}
                    onEdit={canWriteCalendar ? openEdit : undefined}
                    canWriteCalendar={canWriteCalendar}
                />
            ))}
        </div>
    );

    const expandedBody = (
        <div className="mt-4 space-y-4">
            {nonEmptyGroups.map((g) => (
                <section key={g}>
                    <span className="text-xs font-bold uppercase opacity-60">
                        <FormattedMessage id={GROUP_LABEL_ID[g]} />
                    </span>
                    <div className="mt-1 space-y-2">
                        {groups[g].map((event) => (
                            <ExpandedRow
                                key={event.id}
                                event={event}
                                onEdit={canWriteCalendar ? openEdit : undefined}
                                canWriteCalendar={canWriteCalendar}
                            />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );

    return (
        <HomeWidgetShell
            className="HomeCalendarWidget"
            expanded={expanded}
            expandedWidth="w-[640px]"
            header={header}
            subHeader={subHeader}
            collapsed={collapsedBody}
            extras={
                canWriteCalendar ? (
                    <CalendarEventFormModal open={modalOpen} onClose={closeModal} data={editingEvent} />
                ) : null
            }
        >
            {expandedBody}
        </HomeWidgetShell>
    );
};
